/* RESPONSIBLE TEAM: team-inbox-management */
import { type RenderableData } from 'embercom/objects/inbox/renderable-data';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import { EntityType } from 'embercom/models/data/entity-types';
import { type BlockList } from '@intercom/interblocks.ts';
import SendState, { type SendStateWireFormat } from 'embercom/objects/inbox/send-state';
import { tracked } from '@glimmer/tracking';
import AdminSummary, { type AdminSummaryWireFormat } from '../admin-summary';
import CustomBotSummary, {
  type CustomBotSummaryWireFormat,
} from 'embercom/objects/inbox/custom-bot-summary';
import { Channel } from 'embercom/models/data/inbox/channels';
import {
  EmailMetadata,
  type EmailMetadataWireFormat,
} from 'embercom/objects/inbox/renderable/email-metadata';
import { Upload, type UploadWireFormat } from 'embercom/objects/inbox/renderable/upload';
import Tag, { type TagWireFormat } from 'embercom/objects/inbox/tag';
import { type TranslationStatus } from 'embercom/objects/inbox/renderable-data';
import {
  type TranslationQualityFeedbackWireFormat,
  type TranslationQualityFeedback,
  deserializeTranslationQualityFeedbacks,
} from 'embercom/objects/inbox/renderable/translation-quality-feedback-types';

interface CarouselItemWireFormat {
  id?: string | number;
  title?: string;
  subtitle?: string;
  description?: string;
  url?: string;
  image?: string;
}

export class CarouselItem {
  readonly id?: string | number;
  readonly title?: string;
  readonly subtitle?: string;
  readonly description?: string;
  readonly url?: string;
  readonly image?: string;

  constructor(json: CarouselItemWireFormat) {
    this.id = json.id;
    this.title = json.title;
    this.subtitle = json.subtitle;
    this.description = json.description;
    this.url = json.url;
    this.image = json.image;
  }
}

export enum RatingType {
  Positive = 'positive',
  Negative = 'negative',
}

interface AnswerRatingWireFormat {
  answer_id: number;
  answer_title: string;
  rating?: RatingType;
}

export class AnswerRating {
  readonly answerID: number;
  readonly answerTitle: string;
  @tracked rating?: RatingType;

  constructor(answerID: number, answerTitle: string, rating?: RatingType) {
    this.answerID = answerID;
    this.answerTitle = answerTitle;
    this.rating = rating;
  }

  static deserialize(json: AnswerRatingWireFormat): AnswerRating {
    return new AnswerRating(json.answer_id, json.answer_title, json.rating);
  }
}

interface BotCommentWireFormat {
  renderable_type: RenderableType;
  blocks: BlockList;
  entity: AdminSummaryWireFormat;
  send_state?: SendStateWireFormat;
  answer_rating?: AnswerRatingWireFormat;
  custom_bot_summary?: CustomBotSummaryWireFormat;
  readonly channel?: Channel;
  email_metadata?: EmailMetadataWireFormat;
  related_part_id?: number;
  tags: Array<TagWireFormat>;
  uploads?: Array<UploadWireFormat>;
  translation_status?: string;
  carousel_items?: Array<CarouselItemWireFormat>;
  translation_quality_feedbacks?: Record<string, TranslationQualityFeedbackWireFormat>;
  multilingual_workflow_translated?: boolean;
}

export default class BotComment implements RenderableData {
  readonly renderableType: RenderableType = RenderableType.BotComment;
  readonly blocks: BlockList;
  readonly entity: AdminSummary;
  readonly answerRating?: AnswerRating;
  readonly customBotSummary?: CustomBotSummary;
  readonly channel?: Channel;
  readonly relatedPartId?: number;
  readonly uploads?: Array<Upload>;
  readonly carouselItems?: Array<CarouselItem>;

  get hasCarouselItems(): boolean {
    return Array.isArray(this.carouselItems) && this.carouselItems.length > 0;
  }

  get adminSummary() {
    return this.entity;
  }

  @tracked sendState?: SendState;
  @tracked emailMetadata?: EmailMetadata;
  @tracked tags: Array<Tag>;
  readonly translationStatus?: TranslationStatus | string;
  readonly translationQualityFeedbacks?: Record<string, TranslationQualityFeedback>;
  readonly multilingualWorkflowTranslated?: boolean;
  get creatingEntity() {
    return {
      type: EntityType.Bot,
      id: this.entity.id,
      object: this.entity,
    };
  }

  get styles() {
    if (this.hasError) {
      return {
        backgroundColor: 'bg-beta-container opacity-50',
        textColor: 'text-muted',
      };
    }
    if (this.channel === Channel.Email) {
      return {
        backgroundColor: 'bg-beta-container',
        textColor: 'text-default',
        backgroundColorHover: 'bg-beta-container-emphasis',
      };
    }
    return {
      backgroundColor: 'bg-beta-container',
      textColor: 'text-default',
      backgroundColorHover: 'bg-beta-container-emphasis',
    };
  }

  get ticketStyles() {
    return {
      backgroundColor: 'bg-transparent',
      textColor: 'text-default',
      backgroundColorHover: 'bg-neutral-container',
    };
  }

  get hasError() {
    return this.sendState?.sendState === 0;
  }

  updateSendState(newSendState: SendState) {
    this.sendState = newSendState;
  }

  constructor(
    blocks: BlockList,
    entity: AdminSummary,
    tags: Array<Tag>,
    sendState?: SendState,
    answerRating?: AnswerRating,
    customBotSummary?: CustomBotSummary,
    channel?: Channel,
    emailMetadata?: EmailMetadata,
    relatedPartId?: number,
    uploads?: Array<Upload>,
    translationStatus?: TranslationStatus | string,
    carouselItems?: Array<CarouselItem>,
    translationQualityFeedbacks?: Record<string, TranslationQualityFeedback>,
    multilingualWorkflowTranslated?: boolean,
  ) {
    this.blocks = blocks;
    this.entity = entity;
    this.sendState = sendState;
    this.answerRating = answerRating;
    this.customBotSummary = customBotSummary;
    this.channel = channel;
    this.emailMetadata = emailMetadata;
    this.relatedPartId = relatedPartId;
    this.tags = tags || [];
    this.uploads = uploads;
    this.translationStatus = translationStatus;
    this.carouselItems = carouselItems;
    this.translationQualityFeedbacks = translationQualityFeedbacks;
    this.multilingualWorkflowTranslated = multilingualWorkflowTranslated;
  }

  get isRatingRequest() {
    let [block] = this.blocks;
    return this.blocks.length === 1 && block.type === 'conversationRating';
  }

  static deserialize(json: BotCommentWireFormat): BotComment {
    let emailMetadata = json.email_metadata
      ? EmailMetadata.deserialize(json.email_metadata)
      : undefined;
    return new BotComment(
      json.blocks,
      AdminSummary.deserialize(json.entity),
      json.tags && json.tags.map(Tag.deserialize),
      json.send_state && SendState.deserialize(json.send_state),
      json.answer_rating ? AnswerRating.deserialize(json.answer_rating) : undefined,
      json.custom_bot_summary ? CustomBotSummary.deserialize(json.custom_bot_summary) : undefined,
      json.channel,
      emailMetadata,
      json.related_part_id,
      json.uploads?.map(Upload.deserialize),
      json.translation_status,
      json.carousel_items?.map((item) => new CarouselItem(item)),
      deserializeTranslationQualityFeedbacks(json.translation_quality_feedbacks),
      json.multilingual_workflow_translated,
    );
  }
}
